import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { User, Student, Teacher, Admin, UserRole, ApiResponse } from '../../types/portal';
import { apiService } from '../../services/api';

export interface UserState {
    students: Student[];
    teachers: Teacher[];
    admins: Admin[];
    isLoading: boolean;
    error: string | null;
}

export const fetchStudents = createAsyncThunk(
    'user/fetchStudents',
    async (_, { getState, rejectWithValue }) => {
        try {
            const state = getState() as any;
            const role = state.auth?.user?.role;
            const endpoint = (role === UserRole.ADMIN || role === 'ADMINISTRATOR' || role === 'ROLE_ADMINISTRATOR' || role === 'SUPER_ADMIN') 
                ? '/super-admin/api/teachers-and-students' 
                : '/api/teacher/students';
            const resp = await apiService.get<any>(endpoint);
            const rawStudents = Array.isArray(resp?.students) ? resp.students : (Array.isArray(resp) ? resp : []);
            const students: Student[] = rawStudents.map((u: any) => ({
                id: String(u.id),
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
                role: UserRole.STUDENT,
                isActive: u.enabled ?? u.isActive ?? true,
                createdAt: u.createdAt ?? new Date().toISOString(),
                updatedAt: u.updatedAt ?? new Date().toISOString(),
                assignedTeacher: u.assignedTeacher ? String(u.assignedTeacher.id) : null,
                houseNumber: u.houseNumber ?? '',
                dateOfBirth: u.dateOfBirth ?? '',
                postcode: u.postcode ?? '',
                emergencyContact: u.emergencyContact ?? '',
                authTokenResponse: { accessToken: '', refreshToken: '', tokenType: 'Bearer', roles: '', expiresAt: new Date(Date.now()+3600_000).toISOString() }
            }));
            return students;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch students');
        }
    }
);

export const updateUserStatus = createAsyncThunk(
    'user/updateUserStatus',
    async ({ userId, isActive }: { userId: string; isActive: boolean }, { rejectWithValue }) => {
        try {
            const response = await apiService.put<any>(`/super-admin/api/users/${userId}/status`, { isActive });
            if (response && response.success !== false) {
                return { userId, isActive };
            } else {
                return rejectWithValue(response?.error || 'Failed to update user status');
            }
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Network error occurred');
        }
    }
);

export const assignStudentToTeacher = createAsyncThunk(
    'user/assignStudentToTeacher',
    async (
        { studentId, teacherId }: { studentId: string; teacherId: string },
        { dispatch, rejectWithValue }
    ) => {
        try {
            const response = await apiService.post<any>(
                `/super-admin/api/assign-student`,
                { studentId, teacherId }
            );
            if (response && response.success !== false) {
                dispatch(fetchStudents());
                return { studentId, teacherId };
            } else {
                return rejectWithValue(response?.error || 'Failed to assign student to teacher');
            }
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Network error occurred');
        }
    }
);

export const fetchTeachers = createAsyncThunk(
    'user/fetchTeachers',
    async (_, { rejectWithValue }) => {
        try {
            const resp = await apiService.get<any>('/super-admin/api/teachers-and-students');
            const rawTeachers = Array.isArray(resp?.teachers) ? resp.teachers : [];
            const rawStudents = Array.isArray(resp?.students) ? resp.students : [];


// Parse teachers
            const teacherMap: { [id: string]: any } = {};
            rawTeachers.forEach((u: any) => {
                teacherMap[u.id] = {
                    ...u,
                    students: [],
                };
            });
            rawStudents.forEach((s: any) => {
                if (s.assignedTeacher && teacherMap[s.assignedTeacher.id]) {
                    teacherMap[s.assignedTeacher.id].students.push(String(s.id)); // <-- Fix here
                }
            });
            const teachers: Teacher[] = Object.values(teacherMap).map((u: any) => ({
                id: String(u.id),
                email: u.email,
                firstName: u.firstName,
                lastName: u.lastName,
                houseNumber: u.houseNumber ?? '',
                role: UserRole.TEACHER,
                isActive: u.enabled ?? u.isActive ?? true,
                createdAt: u.createdAt ?? new Date().toISOString(),
                updatedAt: u.updatedAt ?? new Date().toISOString(),
                experience: u.experience ?? undefined,
                availability: u.availability ?? [],
                teacherLicenses: u.teacherLicenses ?? [],
                students: u.students,
                authTokenResponse: { accessToken: '', refreshToken: '', tokenType: 'Bearer', roles: '', expiresAt: new Date(Date.now()+3600_000).toISOString() }
            }));
            return teachers;
        } catch (error: any) {
            return rejectWithValue(error?.message || 'Failed to fetch teachers');
        }
    }
);


// Token management utilities
const ACCESS_TOKEN_KEY = 'accessToken';
const ACCESS_TOKEN_EXPIRY_KEY = 'accessTokenExpiresAt';
let tokenExpiryTimeout: ReturnType<typeof setTimeout> | null = null;

function setAccessToken(token: string, expiresAt: string) {
  localStorage.setItem(ACCESS_TOKEN_KEY, token);
  localStorage.setItem(ACCESS_TOKEN_EXPIRY_KEY, expiresAt);
  // Clear any previous timeout
  if (tokenExpiryTimeout) clearTimeout(tokenExpiryTimeout);
  // Set timeout to clear token after expiry
  const expiryTime = new Date(expiresAt).getTime() - Date.now();
  if (expiryTime > 0) {
    tokenExpiryTimeout = setTimeout(() => {
      clearAccessToken();
    }, expiryTime);
  } else {
    clearAccessToken();
  }
}

function clearAccessToken() {
  localStorage.removeItem(ACCESS_TOKEN_KEY);
  localStorage.removeItem(ACCESS_TOKEN_EXPIRY_KEY);
  if (tokenExpiryTimeout) clearTimeout(tokenExpiryTimeout);
}

function checkAndClearExpiredToken() {
  const expiresAt = localStorage.getItem(ACCESS_TOKEN_EXPIRY_KEY);
  if (expiresAt && new Date(expiresAt).getTime() <= Date.now()) {
    clearAccessToken();
  }
}

// Check on load
checkAndClearExpiredToken();
// Define initialState above userSlice
const initialState: UserState = {
    students: [],
    teachers: [],
    admins: [],
    isLoading: false,
    error: null,
};

// User slice
const userSlice = createSlice({
    name: 'user',
    initialState,
    reducers: {
        clearError: (state) => {
            state.error = null;
        },
        logout: (state) => {
            clearAccessToken();
            state.students = [];
            state.teachers = [];
            state.admins = [];
            state.isLoading = false;
            state.error = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(fetchStudents.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchStudents.fulfilled, (state, action) => {
                state.isLoading = false;
                state.students = action.payload;
                state.error = null;
                if (action.payload && action.payload[0]?.authTokenResponse) {
                    const { accessToken, expiresAt } = action.payload[0].authTokenResponse;
                    setAccessToken(accessToken, expiresAt);
                }
            })
            .addCase(fetchStudents.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(fetchTeachers.pending, (state) => {
                state.isLoading = true;
                state.error = null;
            })
            .addCase(fetchTeachers.fulfilled, (state, action) => {
                state.isLoading = false;
                state.teachers = action.payload;
                state.error = null;
                if (action.payload && action.payload[0]?.authTokenResponse) {
                    const { accessToken, expiresAt } = action.payload[0].authTokenResponse;
                    setAccessToken(accessToken, expiresAt);
                }
            })
            .addCase(fetchTeachers.rejected, (state, action) => {
                state.isLoading = false;
                state.error = action.payload as string;
            })
            .addCase(updateUserStatus.fulfilled, (state, action) => {
                const { userId, isActive } = action.payload;
                const student = state.students.find((s: Student) => s.id === userId);
                if (student) {
                    student.isActive = isActive;
                }
                const teacher = state.teachers.find((t: Teacher) => t.id === userId);
                if (teacher) {
                    teacher.isActive = isActive;
                }
            })
            .addCase(assignStudentToTeacher.fulfilled, (state, action) => {
                const { studentId, teacherId } = action.payload;
                const student = state.students.find((s: Student) => s.id === studentId);
                if (student) {
                    student.assignedTeacher = teacherId;
                }
            });
    },
});

export const { clearError, logout } = userSlice.actions;
export default userSlice.reducer;